/**
 * Inactivity Timeout Hook
 *
 * Fires a caller-supplied callback after a configurable idle window. Two
 * activity-signal modes:
 *
 *   1. DOM events (default) — `events` lists DOM events that count as
 *      activity; useful for generic "user is using the page" detection.
 *   2. External signal — pass `events: []` and call the returned `resetTimer`
 *      yourself when a domain-specific intent fires. Lobby/GameLayout use
 *      this so NPC-driven UI churn (auto-scroll on narration) doesn't hold
 *      the AFK timer open — only player WS sends do.
 *
 * Use `shouldPauseTimeout` for windows where idle should not count (creating
 * a session, mid-handoff, validating a connection, etc.).
 */

import { useCallback, useEffect, useRef } from 'react';

export interface InactivityTimeoutConfig {
  /** Required: fired when the user has been idle for `timeoutMs`. */
  onTimeout: () => void;
  /** Idle duration in milliseconds before `onTimeout` fires. */
  timeoutMs: number;
  /**
   * Optional gate. If returns `true`, the timer is reset rather than firing.
   * Read fresh on every potential fire — safe to use against React state.
   */
  shouldPauseTimeout?: () => boolean;
  /** DOM events that count as "activity". Defaults to a sensible set. */
  events?: string[];
  /** Toggle the hook on/off without unmounting. Defaults to `true`. */
  enabled?: boolean;
}

// `scroll` is intentionally excluded. The hook registers handlers on `document`
// with capture-phase, so programmatic auto-scroll (chat panel scrolling to
// bottom on every new narration line) fires it just as readily as a user wheel
// turn. Including it meant in-game NPC narration churn (~every 30s) reset the
// timer indefinitely and the AFK kick never fired — #284. Real user-scroll
// activity is already covered by `mousemove` (cursor moves before/during
// scrolling), `keydown` (PgUp/PgDn/arrow scroll), and `touchstart` (touch
// begins before swipe-scroll), so dropping `scroll` doesn't reduce coverage of
// actual user activity.
const DEFAULT_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'click'];

export function useInactivityTimeout(config: InactivityTimeoutConfig) {
  const {
    onTimeout,
    timeoutMs,
    shouldPauseTimeout,
    events = DEFAULT_EVENTS,
    enabled = true,
  } = config;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Stable refs so the activity-event handlers don't re-bind every render.
  const onTimeoutRef = useRef(onTimeout);
  const shouldPauseRef = useRef(shouldPauseTimeout);
  onTimeoutRef.current = onTimeout;
  shouldPauseRef.current = shouldPauseTimeout;

  const fire = useCallback(() => {
    if (shouldPauseRef.current?.()) {
      // Paused — re-arm and let the next activity reset normally.
      timerRef.current = setTimeout(fire, timeoutMs);
      return;
    }
    onTimeoutRef.current();
  }, [timeoutMs]);

  const reset = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(fire, timeoutMs);
  }, [fire, timeoutMs]);

  // Content-based dep key so callers passing inline `events` literals
  // (e.g. `events: []` for external-signal mode) don't re-arm the timer on
  // every render — without this, any parent re-render tears down + re-runs
  // the effect, which silently kept the AFK timer at full duration during
  // active gameplay.
  const eventsKey = events.join('|');
  useEffect(() => {
    if (!enabled) return;

    reset();

    const listenerOptions: AddEventListenerOptions = { passive: true, capture: true };
    const handler = () => reset();
    for (const event of events) {
      document.addEventListener(event, handler, listenerOptions);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      for (const event of events) {
        document.removeEventListener(event, handler, listenerOptions);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, eventsKey, reset]);

  return { resetTimer: reset };
}
